// @flow
import React, { Children, useContext, type Node } from 'react';
import classnames from 'classnames';
import { isEmpty } from 'lodash';

import ResponsiveContext from 'Components/utils/responsive/responsiveContext';
import ratios from 'Components/utils/ratios/ratios.utils';

import { Badge, type BadgeProps } from 'Components/frameworks/badge/Badge.jsx';

import { Hero } from 'Components/widgets/hero/Hero.jsx';
import {
  ImageSlideShow,
  type ImageSlideShowProps,
} from 'Components/frameworks/image-slideshow/ImageSlideShow.jsx';

import { themes } from './Sheet.themes';

type Props = {
  children: Node,
  className?: string,
  diamond?: BadgeProps,
  footer?: Node,
  immersive: *,
  nextLabel?: string,
  previousLabel?: string,
  slideshow?: ImageSlideShowProps,
  theme?: string,
};

export function Sheet(props: Props) {
  const {
    children,
    className,
    diamond,
    immersive,
    footer,
    slideshow = {},
    theme = 'default',
  } = props;

  const { isDesktop } = useContext(ResponsiveContext);

  if (!Children.count(children)) return null;

  const hasSlideshow = !isEmpty(slideshow.media);

  const {
    thBody,
    thContent,
    thDiamond,
    thDiamondClassName,
    thDiamondSize,
    thFooter,
    thHero,
    thHeroStyles,
    thRoot,
    thSlideshow,
    thSlideshowStyles,
    thTop,
    thTopStyles,
  } = themes[theme]({
    diamond,
    hasSlideshow,
    isDesktop,
  });

  return (
    <section className={classnames(thRoot, className)}>
      {(!isEmpty(immersive) || hasSlideshow) && (
        <div className={thTop} style={thTopStyles}>
          {!isEmpty(immersive) && (
            <div className={thHero}>
              <Hero {...immersive} size={hasSlideshow ? 'full' : 'medium'} style={thHeroStyles} />
            </div>
          )}
          {hasSlideshow && (
            <div className={thSlideshow} style={thSlideshowStyles}>
              <ImageSlideShow media={slideshow.media} ratio={ratios.XL} {...slideshow} />
            </div>
          )}
          {!isEmpty(diamond) && (
            <div className={thDiamond}>
              <Badge
                className={thDiamondClassName}
                size={thDiamondSize}
                {...diamond}
                variant="diamond"
              />
            </div>
          )}
        </div>
      )}
      <div className={thBody} data-testid="Sheet-body">
        <div className={thContent}>{children}</div>
      </div>
      {!!footer && (
        <footer className={thFooter}>
          <div className={thContent}>{footer}</div>
        </footer>
      )}
    </section>
  );
}
